import { memo, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom-v5-compat';
import { ButtonBase, Typography } from '@material-ui/core';
import cx from 'classnames';
import PropTypes from 'prop-types';

import { IconDelete } from 'src/assets/icons/Delete';
import { EditIcon } from 'src/assets/icons/EditIcon';
import { ProjectTypeIcon } from 'src/components/ProjectTypeIcon';
import { PROJECT_TYPE } from 'src/data/project';
import { ROUTES } from 'src/routes';

import { ProjectActionsDropdown } from './components/ProjectActionsDropdown';
import { ProjectDescription } from './components/ProjectDescription';
import { PROJECT_LIST_ITEM_ACTIONS } from './actions';
import { useStyles } from './useStyles';

const propTypes = {
  className: PropTypes.string,
  classes: PropTypes.shape({}),
  project: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    type: PropTypes.oneOf(Object.values(PROJECT_TYPE)),
  }).isRequired,
  onAction: PropTypes.func,
  canBeDeleted: PropTypes.bool,
  withLinks: PropTypes.bool,
};

const defaultProps = {
  className: null,
  classes: null,
  onAction: null,
  canBeDeleted: false,
  withLinks: false,
};

const ProjectListItem = ({ className, classes, project, onAction, canBeDeleted, withLinks }) => {
  const styles = useStyles({ classes });

  const actionsEnabled = Boolean(onAction) && canBeDeleted;

  const rootProps = useMemo(
    () =>
      withLinks
        ? {
            component: Link,
            to: `${ROUTES.PROJECTS}/${project.id}`,
          }
        : null,
    [withLinks, project]
  );

  const handleClick = useCallback(() => {
    onAction?.(PROJECT_LIST_ITEM_ACTIONS.click, project);
  }, [project, onAction]);

  const handleDeleteClick = useCallback(
    (event) => {
      // we don't want wrapping 'Link' to react on this click
      event.preventDefault();
      event.stopPropagation();

      onAction?.(PROJECT_LIST_ITEM_ACTIONS.delete, project);
    },
    [project, onAction]
  );

  const handleRenameClick = useCallback(
    (event) => {
      // we don't want wrapping 'Link' to react on this click
      event.preventDefault();
      event.stopPropagation();

      onAction?.(PROJECT_LIST_ITEM_ACTIONS.rename, project);
    },
    [project, onAction]
  );

  const projectActions = useMemo(() => {
    return [
      {
        label: 'Rename',
        onClick: handleRenameClick,
        icon: <EditIcon />,
      },
      {
        label: 'Delete',
        onClick: handleDeleteClick,
        disabled: !canBeDeleted,
        icon: <IconDelete />,
      },
    ];
  }, [handleDeleteClick, handleRenameClick, canBeDeleted]);

  return (
    <ButtonBase
      className={cx(styles.root, className)}
      onClick={handleClick}
      aria-label="ProjectListItem"
      data-testid="ProjectListItem"
      {...rootProps}
    >
      <div className={styles.content}>
        <div className={styles.nameWrapper}>
          <ProjectTypeIcon
            projectType={project.type}
            className={styles.icon}
          />
          <Typography
            className={styles.name}
            variant="body1"
            aria-label="Name"
          >
            {project.name}
          </Typography>
        </div>
        <ProjectDescription
          className={styles.description}
          project={project}
        />
      </div>

      {actionsEnabled && <ProjectActionsDropdown actions={projectActions} />}
    </ButtonBase>
  );
};

ProjectListItem.propTypes = propTypes;
ProjectListItem.defaultProps = defaultProps;

export default memo(ProjectListItem);
